import React, {
    FunctionComponent,
    useState,
    useEffect,
    useCallback
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';

import { Spinner } from '@client/components/shared/Shared';
import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { isSmScreen } from '@client/helpers/screens';
import { track } from '@client/helpers';
import { ThbLinkType } from '@shared/types/campaign';
import { getTabs, getImagesLoaded } from '@client/redux/modules/mylounge';

import Tabs from './Tabs/Tabs';
import {
    findActiveTab,
    getTabImagesMap,
    buildUrlForThb,
    getTopBreakpointOffset
} from './helpers';
import BannerSlider from './banner-slider';
import BannerContent from './banner-content';
import {
    TAG_NAME_TRACK_MAPPING,
    SCROLL_TRACK_TOP_OFFSET,
    TAB_SWIPE_TRACK_EVENT_NAME,
    TAB_CLICK_TRACK_EVENT_NAME,
    SCROLL_TRACK_EVENT_NAME,
    TabIds
} from './constants';
import {
    OutterWrapper,
    Wrapper,
    InnerWrapper,
    TabsWrapper,
    SpinnerWrapper
} from './Banner.styles';

export const Banner: FunctionComponent<any> = (_, { router }) => {
    // selectors
    const clientWidth = useSelector(getClientDeviceWidth);
    const tabs = useSelector(getTabs);
    const imagesLoaded = useSelector(getImagesLoaded);

    // state
    const [tabChangeTimeout, setTabChangeTimeout] = useState(null);
    const [isTabsVisible, setIsTabsVisible] = useState(false);
    const [floating, setFloating] = useState(false);
    const [scrollTracked, setScrollTracked] = useState(false);
    const [selectedId, setSelectedId] = useState(
        router?.params?.filterTag || TabIds.ALL
    );
    const [activeIndex, setActiveIndex] = useState(
        findActiveTab(tabs, selectedId)
    );
    const [lastIndex, setLastIndex] = useState(null);
    // load only active banner image
    const [tabImages, setTabImages] = useState(
        getTabImagesMap(tabs, activeIndex, false)
    );

    const { thb } = tabs[activeIndex];

    const trackScroll = useCallback(() => {
        if (
            !scrollTracked &&
            (!isSmScreen(clientWidth) ||
                window.scrollY > SCROLL_TRACK_TOP_OFFSET)
        ) {
            setScrollTracked(true);
            trackTabEvent(SCROLL_TRACK_EVENT_NAME, activeIndex);
        }
    }, [scrollTracked, clientWidth]);

    const trackTabEvent = (eventName: string, index: number) => {
        const { id } = tabs[index];

        // TODO: update mapping with tracking team
        track(eventName, {
            campaign_level_2: (TAG_NAME_TRACK_MAPPING as any)[id] || id
        });
    };

    useScrollPosition(
        ({ prevPos, currPos }) => {
            trackScroll();

            const isSmOrLess = isSmScreen(clientWidth);
            const scrollDown = currPos.y > prevPos.y;
            const tabsOffset = scrollDown && isSmOrLess ? 50 : 0;

            const topOffset = getTopBreakpointOffset(clientWidth) + tabsOffset;

            setFloating(currPos.y > topOffset);
            setIsTabsVisible(!isSmOrLess || !scrollDown);
        },
        [trackScroll, clientWidth],
        null,
        true,
        100
    );

    // commponent unmount
    useEffect(
        () => () => {
            if (tabChangeTimeout) {
                clearTimeout(tabChangeTimeout);
                setTabChangeTimeout(null);
            }
        },
        []
    );

    // changing tab
    useEffect(() => {
        const newSelectedFilter = router?.params?.filterTag || TabIds.ALL;
        if (selectedId !== newSelectedFilter) {
            setSelectedId(newSelectedFilter);
            const newActiveIndex = findActiveTab(tabs, newSelectedFilter);
            if (newActiveIndex !== activeIndex) {
                setActiveIndex(newActiveIndex);
                setLastIndex(activeIndex);
                setTabImages({
                    ...tabImages,
                    [newActiveIndex]: true
                });
            }
        }
    }, [router?.params?.filterTag]);

    // unlock loading all banner images
    useEffect(() => {
        if (imagesLoaded) {
            setTabImages(getTabImagesMap(tabs, activeIndex, true));
        }
    }, [imagesLoaded]);

    const trackBannerClick = () => {
        track('co-discover-button', {
            brandClicked: thb.id
        });
    };

    const navigateToThbTarget = useCallback(() => {
        if (!thb) {
            return;
        }

        trackBannerClick();
        const url = buildUrlForThb(thb, selectedId);

        if (!url) {
            return;
        }

        if (thb.type === ThbLinkType.TAB) {
            router.push(url);
        } else {
            window.location.href = url;
        }
    }, [thb, activeIndex, selectedId]);

    const changeActiveTab = (newActiveIndex: number, timeout = 200) => {
        setActiveIndex(newActiveIndex);
        setLastIndex(activeIndex);
        setTabImages({
            ...tabImages,
            [newActiveIndex]: true
        });

        const { id } = tabs[newActiveIndex];

        setTabChangeTimeout(
            setTimeout(() => {
                router.push(`/event/${id}${window.location.search}`);

                setScrollTracked(false);
            }, timeout)
        );
    };

    const isSameTab = (newActiveIndex: number) =>
        newActiveIndex === activeIndex;

    const handleTabClick = useCallback(
        (newActiveIndex: number, timeout: number) => {
            if (isSameTab(newActiveIndex)) {
                return;
            }

            trackTabEvent(TAB_CLICK_TRACK_EVENT_NAME, newActiveIndex);
            changeActiveTab(newActiveIndex, timeout);
        },
        [activeIndex, tabs, tabImages]
    );

    const handleTabSwipe = useCallback(
        (newActiveIndex: number, timeout: number) => {
            if (isSameTab(newActiveIndex)) {
                return;
            }

            trackTabEvent(TAB_SWIPE_TRACK_EVENT_NAME, newActiveIndex);
            changeActiveTab(newActiveIndex, timeout);
        },
        [activeIndex, tabs, tabImages]
    );

    if (!tabs || !tabs.length || !tabs[activeIndex]) {
        return (
            <SpinnerWrapper>
                <Spinner black={true} />
            </SpinnerWrapper>
        );
    }

    return (
        <OutterWrapper>
            <Wrapper>
                <BannerSlider
                    tabs={tabs}
                    activeIndex={activeIndex}
                    lastIndex={lastIndex}
                    onBannerSwipe={handleTabSwipe}
                    tabImages={tabImages}
                    onImageClick={navigateToThbTarget}
                />
                <InnerWrapper>
                    <BannerContent
                        thb={thb}
                        onButtonClick={navigateToThbTarget}
                    />
                </InnerWrapper>
            </Wrapper>
            <TabsWrapper
                floating={floating}
                floatingHidden={!isTabsVisible && floating}
            >
                <Tabs
                    tabs={tabs}
                    onTabClick={handleTabClick}
                    activeIndex={activeIndex}
                    lastIndex={lastIndex}
                />
            </TabsWrapper>
        </OutterWrapper>
    );
};

Banner.contextTypes = {
    router: PropTypes.object
};

export default Banner;
